import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Upload, Disc3, Sparkles, BadgeCheck, AlertTriangle, RefreshCcw, Loader2, CheckSquare, X as XIcon, Trash2, BarChart3, Video, ChevronLeft, HelpCircle, Apple } from 'lucide-react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import GenreAlbumTile from '@/components/GenreAlbumTile';
import CoverPickerModal from '@/components/CoverPickerModal';
import MusicCardCompact from '@/components/MusicCardCompact';
import MusicCardDetail from '@/components/MusicCardDetail';
import CrateSearchBar from '@/components/CrateSearchBar';
import InstallPwaBanner from '@/components/InstallPwaBanner';
import TrialBanner from '@/components/TrialBanner';
import RecheckSuggestionsModal from '@/components/RecheckSuggestionsModal';
import { HelpButton } from '@/components/HowItWorks';
import SnapAndSeekButton from '@/components/SnapAndSeekButton';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { ROW_STAGGER_MS, BUTTON_ENTRANCE_DURATION_MS } from '@/lib/motion';

const EMPTY_FILTERS = { q: '', bpm_min: '', bpm_max: '', key: '', source: '', want: false };

// Cap per-tile stagger so the last card in a huge search result doesn't
// wait several seconds to fade in. 15 items × 20ms = 300ms max cascade —
// long enough to feel choreographed on any visible slice of the grid,
// short enough that off-screen items don't feel "sluggish" if the user
// scrolls immediately. Tiles past the cap all fire at the max delay.
const TILE_STAGGER_CAP = 15;

/**
 * Inline style + className helper for the tailwindcss-animate cascade
 * used on the crate grids. Uses CSS animations (respecting Tailwind's
 * `motion-reduce:` variant) instead of framer-motion so we don't inflate
 * the render tree with hundreds of motion nodes on a big collection.
 * `fill-mode-both` (from tailwindcss-animate) holds the initial hidden
 * state before the delay and the final visible state after finishing.
 */
function tileCascadeProps(index) {
  return {
    className:
      'animate-in fade-in slide-in-from-bottom-2 fill-mode-both motion-reduce:animate-none',
    style: {
      animationDuration: `${BUTTON_ENTRANCE_DURATION_MS}ms`,
      animationDelay: `${Math.min(index, TILE_STAGGER_CAP) * ROW_STAGGER_MS}ms`,
    },
  };
}

export default function Crate() {
  const { user } = useAuth();
  const [albums, setAlbums] = useState([]);
  const [stats, setStats] = useState({ owned_count: 0, unidentified_count: 0, needs_review_count: 0 });
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [previewingId, setPreviewingId] = useState(null);
  const [selected, setSelected] = useState(null);
  // Bulk-select state — only meaningful while viewing search results
  // (most useful context for batch cleanup). Toggling off the select mode
  // clears the selection.
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkActing, setBulkActing] = useState(false);

  // Cover picker modal state — opened from the pencil overlay on any tile.
  const [coverPickerOpen, setCoverPickerOpen] = useState(false);
  const [coverPickerCrate, setCoverPickerCrate] = useState(null); // { bucket, art, coverType, coverConfig, coverPath }

  // Maintenance toolbar state — these tools used to live on the Profile
  // page but were moved here because they operate on the collection.
  const [bpmBackfilling, setBpmBackfilling] = useState(false);
  const [videoBackfilling, setVideoBackfilling] = useState(false);
  const [appleBackfilling, setAppleBackfilling] = useState(false);

  const handleBpmBackfill = async () => {
    setBpmBackfilling(true);
    try {
      const { data } = await api.post('/cards/bpm-backfill');
      toast.success(
        `Looked up ${data.checked} ${data.checked === 1 ? 'track' : 'tracks'} · ${data.enriched} got BPM/Key data`
      );
      // Refresh search results so newly-enriched tracks become filterable
      fetchAlbums();
      if (isFiltering) fetchSearchResults();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'BPM lookup failed');
    } finally {
      setBpmBackfilling(false);
    }
  };

  const handleVideoBackfill = async () => {
    setVideoBackfilling(true);
    try {
      const { data } = await api.post('/cards/video-backfill');
      toast.success(
        `Checked ${data.checked} ${data.checked === 1 ? 'track' : 'tracks'} · ${data.enriched} got music videos`
      );
      fetchAlbums();
      if (isFiltering) fetchSearchResults();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Video lookup failed');
    } finally {
      setVideoBackfilling(false);
    }
  };

  const handleAppleMusicBackfill = async () => {
    // Sweep the user's cards for Apple Music deep links already
    // cached from previous Songstats requests. Costs ZERO extra
    // Songstats calls — this is why we can safely offer it as a
    // one-tap action across the entire collection.
    setAppleBackfilling(true);
    try {
      const { data } = await api.post('/cards/apple-music/backfill-cached');
      const updated = data?.updated ?? 0;
      const scanned = data?.scanned ?? 0;
      if (updated > 0) {
        toast.success(
          `Added Apple Music links to ${updated} ${updated === 1 ? 'track' : 'tracks'} · scanned ${scanned}`
        );
      } else {
        toast.info(
          scanned === 0
            ? 'No cards needed backfilling — you\u2019re all set.'
            : `Scanned ${scanned} — none had Apple Music links in the Songstats cache yet.`
        );
      }
      fetchAlbums();
      if (isFiltering) fetchSearchResults();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Apple Music backfill failed');
    } finally {
      setAppleBackfilling(false);
    }
  };

  const toggleCardSelected = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAllVisible = () => {
    setSelectedIds(new Set(searchResults.map((c) => c.id)));
  };

  const clearSelection = () => setSelectedIds(new Set());

  const exitSelectMode = () => {
    setSelectMode(false);
    setSelectedIds(new Set());
  };

  // Re-check (genre reclassification suggestions) modal state.
  // We fetch suggestions on demand, then open the modal with them.
  // The modal mutates its own copy of the list as the user accepts/keeps,
  // so we just need to seed it and refresh albums on close.
  const [recheckOpen, setRecheckOpen] = useState(false);
  const [recheckLoading, setRecheckLoading] = useState(false);
  const [recheckSuggestions, setRecheckSuggestions] = useState([]);

  const isFiltering = !!(
    filters.q || filters.bpm_min || filters.bpm_max || filters.key || filters.source || filters.want
  );

  const fetchAlbums = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/cards/albums');
      setAlbums(data.albums || []);
      setStats({
        owned_count: data.owned_count || 0,
        unidentified_count: data.unidentified_count || 0,
        needs_review_count: data.needs_review_count || 0,
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load collection');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchSearchResults = useCallback(async () => {
    setSearching(true);
    try {
      const params = { status: 'active' };
      if (filters.q) params.q = filters.q;
      if (filters.bpm_min) params.bpm_min = filters.bpm_min;
      if (filters.bpm_max) params.bpm_max = filters.bpm_max;
      if (filters.key) params.key = filters.key;
      if (filters.source) params.source = filters.source;
      if (filters.want) params.want = true;
      const { data } = await api.get('/cards', { params });
      setSearchResults(data.cards || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Search failed');
    } finally {
      setSearching(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchAlbums();
  }, [fetchAlbums]);

  // Debounced search when any filter changes
  useEffect(() => {
    if (!isFiltering) {
      setSearchResults([]);
      // Bulk select mode is only meaningful while filtering — auto-exit
      // when the user clears the search so the action bar disappears too.
      if (selectMode) {
        setSelectMode(false);
        setSelectedIds(new Set());
      }
      return;
    }
    const handle = setTimeout(() => fetchSearchResults(), 250);
    return () => clearTimeout(handle);
  }, [filters, isFiltering, fetchSearchResults, selectMode]);

  const handleExportCSV = async () => {
    try {
      const resp = await api.get('/cards/export.csv', { responseType: 'blob' });
      const blob = resp?.data;
      if (!blob) throw new Error('Empty response from server');
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const dateStr = new Date().toISOString().slice(0, 10);
      a.href = url;
      a.download = `groovlens-collection-${dateStr}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('Collection exported');
    } catch (e) {
      const status = e?.response?.status;
      const detail = status ? `HTTP ${status}` : (e?.message || 'unknown');
      toast.error(`Export failed: ${detail}`);
    }
  };

  const handleSelect = (card) => {
    setSelected(card);
  };

  const handleDelete = async (card) => {
    if (!window.confirm('Delete this card from your collection?')) return;
    try {
      await api.delete(`/cards/${card.id}`);
      toast.success('Card deleted');
      setSelected(null);
      fetchAlbums();
      if (isFiltering) fetchSearchResults();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to delete');
    }
  };

  const handleMarkOwned = async (card) => {
    try {
      await api.post(`/cards/${card.id}/owned`);
      toast.success(`Filed as Owned: ${card.title}`);
      setSelected(null);
      fetchAlbums();
      if (isFiltering) fetchSearchResults();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to mark owned');
    }
  };

  const handleGenreChange = async (card, newBucket) => {
    try {
      const { data } = await api.patch(`/cards/${card.id}/genre`, {
        genre_bucket: newBucket,
      });
      toast.success(`Moved to ${newBucket} crate`);
      // Refresh selected card so the modal shows the new bucket immediately
      setSelected(data || { ...card, genre_bucket: newBucket });
      fetchAlbums();
      if (isFiltering) fetchSearchResults();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to change genre');
    }
  };

  const handleToggleWant = async (card) => {
    // Optimistic — flip the flag locally on both the row and the open modal.
    const prevWant = !!card.want;
    setSearchResults((prev) => prev.map((c) => (c.id === card.id ? { ...c, want: !prevWant } : c)));
    setSelected((sel) => (sel && sel.id === card.id ? { ...sel, want: !prevWant } : sel));
    try {
      const { data } = await api.post(`/cards/${card.id}/toggle-want`);
      toast.success(data?.want ? 'Added to want list' : 'Removed from want list');
      setSearchResults((prev) => prev.map((c) => (c.id === card.id ? { ...c, want: !!data?.want } : c)));
      setSelected((sel) => (sel && sel.id === card.id ? { ...sel, want: !!data?.want } : sel));
    } catch (e) {
      setSearchResults((prev) => prev.map((c) => (c.id === card.id ? { ...c, want: prevWant } : c)));
      setSelected((sel) => (sel && sel.id === card.id ? { ...sel, want: prevWant } : sel));
      toast.error(e?.response?.data?.detail || 'Failed to update want list');
    }
  };

  // Fetch genre-reclassification suggestions across the whole collection
  // (no `bucket` param = all crates). Three outcomes per spec:
  //   • 0 suggestions       → "Crate lookin' good!" toast (3s, no interruption)
  //   • 1\u20132 suggestions      → toast with inline "View N suggestions" action
  //                            (don't full-modal-interrupt for tiny changes)
  //   • 3+ suggestions      → open the full review modal directly
  const runRecheck = async () => {
    setRecheckLoading(true);
    try {
      const { data } = await api.post('/cards/reclassify-suggestions');
      const list = data?.suggestions || [];
      const n = list.length;
      if (n === 0) {
        toast.success("\ud83d\udc4d Crate lookin' good!", { duration: 3000 });
        return;
      }
      if (n < 3) {
        // Soft surface for tiny changes \u2014 stick the link in the toast so the
        // user can ignore it if they want; tapping View opens the modal.
        toast(`Found ${n} ${n === 1 ? 'track' : 'tracks'} that might belong elsewhere`, {
          duration: 8000,
          action: {
            label: `View ${n} ${n === 1 ? 'suggestion' : 'suggestions'}`,
            onClick: () => {
              setRecheckSuggestions(list);
              setRecheckOpen(true);
            },
          },
        });
        return;
      }
      setRecheckSuggestions(list);
      setRecheckOpen(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Re-check failed');
    } finally {
      setRecheckLoading(false);
    }
  };

  // Bulk action handlers ------------------------------------------------
  const handleBulkDelete = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    if (!window.confirm(`Delete ${ids.length} ${ids.length === 1 ? 'track' : 'tracks'} from your collection?`)) return;
    setBulkActing(true);
    try {
      const { data } = await api.post('/cards/bulk-delete', { card_ids: ids });
      toast.success(`Deleted ${data.deleted} ${data.deleted === 1 ? 'track' : 'tracks'}`);
      exitSelectMode();
      fetchAlbums();
      if (isFiltering) fetchSearchResults();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Bulk delete failed');
    } finally {
      setBulkActing(false);
    }
  };

  const handleBulkMarkOwned = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    setBulkActing(true);
    try {
      const { data } = await api.post('/cards/bulk-mark-owned', { card_ids: ids });
      toast.success(`Marked ${data.marked_owned} ${data.marked_owned === 1 ? 'track' : 'tracks'} as owned`);
      exitSelectMode();
      fetchAlbums();
      if (isFiltering) fetchSearchResults();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Bulk mark-owned failed');
    } finally {
      setBulkActing(false);
    }
  };

  const total = albums.reduce((s, a) => s + (a.count || 0), 0);

  return (
    <div>
      {/* Trial status banner — shown only while user is on a trial, escalates as days run out */}
      <TrialBanner />
      {/* PWA install banner — shows only when installable + not yet dismissed */}
      <InstallPwaBanner />

      {/* Header
          Stack vertically on small screens so the title doesn't get squeezed
          by the action buttons. On sm+ they sit side-by-side as before.

          Post-login splash — fades + scales in on first mount with the
          same 100ms ease-out spec used by every modal + the landing
          hero, so returning users get the same choreographed opening
          beat. Reduced-motion users skip via `motion-reduce:animate-none`. */}
      <div
        className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between mb-5 animate-in fade-in zoom-in-95 duration-100 fill-mode-both motion-reduce:animate-none"
        data-testid="crate-hero"
      >
        <div className="min-w-0">
          <h1 className="font-display text-2xl sm:text-4xl font-semibold tracking-tight">
            Your Collection
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {user?.name ? `Welcome back, ${user.name}.` : 'Welcome back.'}{' '}
            {total > 0
              ? `${total} ${total === 1 ? 'pull' : 'pulls'} across ${albums.length} ${albums.length === 1 ? 'genre crate' : 'genre crates'}`
              : 'Start by scanning some screenshots.'}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {/* Two peer scan verbs, color-coded so users parse them instantly:
              amber "Scan Screenshots" (camera-roll uploads) + teal
              "Snap & Seek" (live-camera captures). Same downstream pipeline,
              different capture source. */}
          <Link to="/upload" className="flex-1 sm:flex-none">
            <Button
              data-testid="home-upload-cta"
              className="rounded-xl w-full sm:w-auto border-2 border-[#FFB24A] !bg-transparent text-[#FFB24A] hover:!bg-[#FFB24A]/35 hover:text-white active:!bg-[#FFB24A]/35 active:text-white focus-visible:!bg-[#FFB24A]/35 focus-visible:text-white focus-visible:ring-[#FFB24A] hover:shadow-[0_0_24px_-8px_#FFB24A] transition-colors duration-150"
            >
              <Upload className="w-4 h-4 mr-2" />
              <span className="sm:hidden">Scan</span>
              <span className="hidden sm:inline">Scan Screenshots</span>
            </Button>
          </Link>
          <SnapAndSeekButton
            className="w-full sm:w-auto"
            testid="crate-snap-and-seek"
          />
        </div>
      </div>

      {/* Search bar */}
      {(total > 0 || isFiltering) && (
        <div className="mb-4 max-w-4xl mx-auto">
          <CrateSearchBar
            filters={filters}
            onChange={setFilters}
            onExportCSV={total > 0 ? handleExportCSV : null}
            placeholder="Search across all your crates…"
          />
        </div>
      )}

      {/* Tools row — Re-check + BPM lookup + Video lookup. Grouped together
          so users discover them as "things I can do to my collection" rather
          than buried as maintenance. Only renders when the user has tracks.
          On mobile, BPM/Video shrink to icon-only so all three buttons fit
          on one line. */}
      {total > 0 && (
        <div
          className="mb-4 max-w-4xl mx-auto rounded-xl border border-border bg-card p-2.5 flex items-center gap-2 flex-nowrap overflow-x-auto"
          data-testid="crate-tools-bar"
        >
          <Button
            size="sm"
            variant="outline"
            onClick={runRecheck}
            disabled={recheckLoading}
            className="rounded-lg h-8 text-xs shrink-0"
            data-testid="crate-recheck-all-button"
            title="Re-check every track's genre against current tags"
          >
            {recheckLoading ? (
              <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
            ) : (
              <RefreshCcw className="w-3.5 h-3.5 mr-1.5" />
            )}
            Re-check <span className="hidden sm:inline ml-1">all</span>
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={handleBpmBackfill}
            disabled={bpmBackfilling}
            className="rounded-lg h-8 text-xs shrink-0"
            data-testid="crate-bpm-backfill-button"
            title="Look up BPM and key for every track in your collection"
            aria-label="Look up BPM and key"
          >
            {bpmBackfilling ? (
              <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
            ) : (
              <RefreshCcw className="w-3.5 h-3.5 mr-1.5" />
            )}
            <span className="sm:hidden">BPM</span>
            <span className="hidden sm:inline">Look up BPM &amp; Key</span>
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={handleVideoBackfill}
            disabled={videoBackfilling}
            className="rounded-lg h-8 text-xs shrink-0"
            data-testid="crate-video-backfill-button"
            title="Find a YouTube music video for every track in your collection"
            aria-label="Find music videos"
          >
            {videoBackfilling ? (
              <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
            ) : (
              <Video className="w-3.5 h-3.5 mr-1.5" />
            )}
            <span className="sm:hidden">Video</span>
            <span className="hidden sm:inline">Find Music videos</span>
          </Button>

          <Button
            size="sm"
            variant="outline"
            onClick={handleAppleMusicBackfill}
            disabled={appleBackfilling}
            className="rounded-lg h-8 text-xs shrink-0"
            data-testid="crate-apple-music-backfill-button"
            title="Add Apple Music links to tracks using data already cached in this app (no extra Songstats calls)"
            aria-label="Backfill Apple Music links"
          >
            {appleBackfilling ? (
              <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
            ) : (
              <Apple className="w-3.5 h-3.5 mr-1.5" />
            )}
            <span className="sm:hidden">Apple</span>
            <span className="hidden sm:inline">Backfill Apple links</span>
          </Button>

          {/* Review inbox — always visible in the tools bar so users know
              it exists, even when the queue is empty. Count badge next to
              the label appears when there ARE tracks to review, and
              disappears when the queue is clear (user request). Uses
              Link asChild instead of Button so it navigates like a normal
              route while keeping button styling. */}
          <Link
            to="/review"
            className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-background h-8 px-2.5 text-xs shrink-0 hover:bg-secondary/60 hover:border-[hsl(var(--primary))]/40 transition-colors duration-150"
            data-testid="crate-review-inbox-link"
            title="Approve or edit tracks that couldn't be strictly verified"
            aria-label={
              stats.needs_review_count > 0
                ? `Review inbox — ${stats.needs_review_count} track${stats.needs_review_count === 1 ? '' : 's'} pending`
                : 'Review inbox'
            }
          >
            <HelpCircle className="w-3.5 h-3.5" />
            <span className="sm:hidden">Review</span>
            <span className="hidden sm:inline">Review</span>
            {stats.needs_review_count > 0 && (
              <span
                className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-mono font-semibold bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))]"
                data-testid="crate-review-inbox-count"
              >
                {stats.needs_review_count}
              </span>
            )}
          </Link>
        </div>
      )}

      {/* Stat strip — hide when actively filtering to keep focus on results */}
      {!loading && total > 0 && !isFiltering && (
        <>
        <div className="grid grid-cols-3 gap-2 sm:gap-3 mb-3">
          <div className="rounded-xl border border-border bg-card p-3 sm:p-4" data-testid="stat-total">
            <div className="text-xs text-muted-foreground inline-flex items-center gap-1.5">
              <Disc3 className="w-3.5 h-3.5" /> Total
            </div>
            <div className="font-display text-2xl font-semibold mt-0.5">{total}</div>
          </div>
          <Link to="/owned" className="rounded-xl border border-border bg-card p-3 sm:p-4 hover:border-[var(--brand-lime)]/40 transition-colors duration-200" data-testid="stat-owned">
            <div className="text-xs text-muted-foreground inline-flex items-center gap-1.5">
              <BadgeCheck className="w-3.5 h-3.5" /> Owned
            </div>
            <div className="font-display text-2xl font-semibold mt-0.5">{stats.owned_count}</div>
          </Link>
          <Link to="/unidentified" className="rounded-xl border border-border bg-card p-3 sm:p-4 hover:border-[hsl(var(--destructive))]/40 transition-colors duration-200" data-testid="stat-unidentified">
            <div className="text-xs text-muted-foreground inline-flex items-center gap-1.5">
              <AlertTriangle className="w-3.5 h-3.5" /> To fix
            </div>
            <div className="font-display text-2xl font-semibold mt-0.5">{stats.unidentified_count}</div>
          </Link>
        </div>
        <div className="mb-6 flex justify-end">
          <Link
            to="/stats"
            className="inline-flex items-center gap-1.5 text-xs font-mono font-medium text-[hsl(var(--primary))] underline underline-offset-4 decoration-[hsl(var(--primary))]/40 hover:text-[var(--brand-lime)] hover:decoration-[var(--brand-lime)] transition-colors"
            data-testid="crate-view-stats-link"
          >
            <BarChart3 className="w-3.5 h-3.5" />
            View full stats &rarr;
          </Link>
        </div>
        </>
      )}

      {/* Search results view */}
      {isFiltering ? (
        <div className="max-w-4xl mx-auto" data-testid="crate-search-results">
          {/* Back to collection — exits search mode. Clearing the filters
              via `EMPTY_FILTERS` also collapses the search results panel
              and shows the genre grid again. */}
          <div className="mb-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setFilters(EMPTY_FILTERS);
                clearSelection();
              }}
              className="rounded-lg h-8 text-xs pl-2 -ml-2"
              data-testid="crate-back-to-collection"
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              Back to collection
            </Button>
          </div>

          {/* BPM-filter hint banner — surfaced when the user has applied
              a BPM range AND we got back very few results, AND we know
              they have a meaningful number of tracks. Most likely cause:
              their tracks don't have BPM data yet (only populated when
              the user runs "Look up BPM & Key" from Profile, or the
              auto-enrich grabbed it during scan for the lucky ones). */}
          {(filters.bpm_min || filters.bpm_max) && !searching && searchResults.length <= 2 && total >= 5 && (
            <div
              className="mb-3 rounded-xl border border-[var(--brand-amber)]/40 bg-[var(--brand-amber)]/10 p-3 flex items-start gap-3"
              data-testid="bpm-hint-banner"
            >
              <AlertTriangle className="w-4 h-4 text-[var(--brand-amber)] mt-0.5 shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium text-foreground">
                  Few BPM matches? Most tracks may not have BPM data yet.
                </div>
                <p className="text-xs text-muted-foreground mt-0.5">
                  BPM is looked up automatically during scans for some tracks but skipped for others.
                  Click <span className="font-medium text-foreground">&ldquo;Look up BPM &amp; Key&rdquo;</span> in
                  the Maintenance toolbar above to fill in the missing values, then re-try this filter.
                </p>
              </div>
            </div>
          )}
          {searching ? (
            <div className="space-y-2">
              {[1, 2, 3, 4].map((i) => (
                <Skeleton key={`search-skeleton-${i}`} className="h-20 rounded-xl" />
              ))}
            </div>
          ) : searchResults.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-border bg-card p-10 text-center">
              <Disc3 className="w-10 h-10 mx-auto text-muted-foreground mb-2" />
              <h3 className="font-display text-lg font-semibold">No matches</h3>
              <p className="text-sm text-muted-foreground">Try clearing some filters.</p>
              <Button
                variant="outline"
                onClick={() => setFilters(EMPTY_FILTERS)}
                className="mt-4 rounded-xl"
                data-testid="crate-clear-filters"
              >
                Clear filters
              </Button>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between mb-2 gap-2">
                <div className="text-xs text-muted-foreground font-mono">
                  {searchResults.length} {searchResults.length === 1 ? 'match' : 'matches'}
                  {selectMode && selectedIds.size > 0 && (
                    <span className="ml-2 text-[hsl(var(--primary))]">
                      · {selectedIds.size} selected
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-1.5">
                  {selectMode ? (
                    <>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={selectAllVisible}
                        data-testid="bulk-select-all"
                        className="rounded-lg h-8 text-xs"
                      >
                        Select all
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={clearSelection}
                        disabled={selectedIds.size === 0}
                        data-testid="bulk-clear-selection"
                        className="rounded-lg h-8 text-xs"
                      >
                        Clear
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={exitSelectMode}
                        data-testid="bulk-exit-select"
                        className="rounded-lg h-8 text-xs"
                      >
                        <XIcon className="w-3.5 h-3.5 mr-1" /> Done
                      </Button>
                    </>
                  ) : (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setSelectMode(true)}
                      data-testid="bulk-enter-select"
                      className="rounded-lg h-8 text-xs"
                    >
                      <CheckSquare className="w-3.5 h-3.5 mr-1" /> Select
                    </Button>
                  )}
                </div>
              </div>
              <div className={`space-y-2 ${selectMode ? 'pb-24' : ''}`}>
                {searchResults.map((card, i) => (
                  <div key={card.id} {...tileCascadeProps(i)}>
                    <MusicCardCompact
                      card={card}
                      isPreviewing={previewingId === card.id}
                      onPreviewToggle={(id) => setPreviewingId(id === previewingId ? null : id)}
                      onOpen={handleSelect}
                      onMarkOwned={handleMarkOwned}
                      onDelete={handleDelete}
                      selectable={selectMode}
                      isSelected={selectedIds.has(card.id)}
                      onToggleSelect={toggleCardSelected}
                    />
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      ) : loading ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Skeleton key={`skeleton-album-${i}`} className="aspect-square rounded-2xl" />
          ))}
        </div>
      ) : albums.length === 0 ? (
        <EmptyState />
      ) : (
        <>
          {/* Hint: users can generate AI cover art per crate. Non-intrusive,
              only rendered when the user actually has crates to customize. */}
          <div
            className="mb-3 flex items-center gap-2 text-xs text-muted-foreground"
            data-testid="crate-cover-hint"
          >
            <Sparkles className="w-3.5 h-3.5 text-[hsl(var(--primary))]" />
            <span>
              Tip: hover any crate and tap the pencil to change its cover (colors, gradients, upload, AI, and more).
            </span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4" data-testid="genre-album-grid">
            {albums.map((a, i) => (
              <div key={a.genre_bucket} {...tileCascadeProps(i)}>
                <GenreAlbumTile
                  bucket={a.genre_bucket}
                  count={a.count}
                  art={a.art || []}
                  coverPath={a.cover_path || null}
                  coverType={a.cover_type || 'default_collage'}
                  coverConfig={a.cover_config || {}}
                  onEditCover={(payload) => {
                    setCoverPickerCrate(payload);
                    setCoverPickerOpen(true);
                  }}
                />
              </div>
            ))}
          </div>
        </>
      )}

      {/* Card detail modal */}
      <MusicCardDetail
        card={selected}
        open={!!selected}
        onClose={() => setSelected(null)}
        onMarkOwned={handleMarkOwned}
        onDelete={handleDelete}
        onGenreChange={handleGenreChange}
        onToggleWant={handleToggleWant}
        // Deck navigation only makes sense while browsing filtered search
        // results (there's no linear "deck" while looking at the genre
        // grid). We pass the current results as the deck; when no search
        // is active the modal is opened from tile flows one-at-a-time.
        deck={isFiltering ? searchResults : null}
        deckIndex={selected && isFiltering ? searchResults.findIndex((c) => c.id === selected.id) : -1}
        onNavigate={(newCard) => setSelected(newCard)}
      />

      {/* Re-check (genre reclassification suggestions) modal */}
      <RecheckSuggestionsModal
        open={recheckOpen}
        onOpenChange={setRecheckOpen}
        scope="all"
        suggestions={recheckSuggestions}
        onAfterAction={() => {
          fetchAlbums();
          if (isFiltering) fetchSearchResults();
        }}
      />
      {/* Floating "?" help button — bottom-right, opens the How GroovLens
          Works modal. Renders on the Crate (post-login home) page only,
          per UX call to keep the help affordance contextual and not global. */}
      <HelpButton />

      {/* Sticky bulk-action bar — visible when select mode is on, anchored
          above the mobile bottom nav with safe-area inset padding. Stays
          fully visible on desktop too. */}
      {selectMode && (
        <div
          className="fixed left-0 right-0 z-[9500] px-4 sm:px-6 pointer-events-none"
          style={{
            bottom: 'calc(5rem + env(safe-area-inset-bottom))',
          }}
          data-testid="bulk-action-bar"
        >
          <div className="max-w-2xl mx-auto pointer-events-auto">
            <div className="rounded-2xl border border-border bg-card shadow-xl p-3 flex items-center gap-2 backdrop-blur">
              <div className="text-sm font-mono text-muted-foreground px-2 shrink-0">
                <span className="text-[hsl(var(--primary))] font-semibold">{selectedIds.size}</span> selected
              </div>
              <div className="flex-1" />
              <Button
                size="sm"
                variant="secondary"
                onClick={handleBulkMarkOwned}
                disabled={selectedIds.size === 0 || bulkActing}
                data-testid="bulk-mark-owned"
                className="rounded-lg"
              >
                {bulkActing ? (
                  <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                ) : (
                  <BadgeCheck className="w-4 h-4 mr-1.5" />
                )}
                Mark owned
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={handleBulkDelete}
                disabled={selectedIds.size === 0 || bulkActing}
                data-testid="bulk-delete"
                className="rounded-lg text-[hsl(var(--destructive))] border-[hsl(var(--destructive))]/40 hover:bg-[hsl(var(--destructive))]/10 hover:text-[hsl(var(--destructive))]"
              >
                {bulkActing ? (
                  <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                ) : (
                  <Trash2 className="w-4 h-4 mr-1.5" />
                )}
                Delete
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Cover Picker modal — opened by pencil overlay on any tile */}
      <CoverPickerModal
        open={coverPickerOpen}
        onClose={() => setCoverPickerOpen(false)}
        bucket={coverPickerCrate?.bucket || ''}
        art={coverPickerCrate?.art || []}
        initialCover={{
          cover_type: coverPickerCrate?.coverType || 'default_collage',
          cover_config: coverPickerCrate?.coverConfig || {},
          cover_path: coverPickerCrate?.coverPath || null,
        }}
        onApplied={(newCover) => {
          // Optimistic local update so the tile reflects the new cover
          // without a full /cards/albums refetch. We do refetch in the
          // background to catch any server-side mutations (history etc).
          if (!coverPickerCrate?.bucket) return;
          setAlbums((prev) =>
            prev.map((a) =>
              a.genre_bucket === coverPickerCrate.bucket
                ? {
                    ...a,
                    cover_path: newCover?.cover_path ?? null,
                    cover_type: newCover?.cover_type || 'default_collage',
                    cover_config: newCover?.cover_config || {},
                  }
                : a,
            ),
          );
          // Silent refetch to stay in sync with server.
          api.get('/cards/albums').then(({ data }) => setAlbums(data.albums || [])).catch(() => {});
        }}
      />
    </div>
  );
}

function EmptyState() {
  return (
    <div
      className="relative rounded-2xl border border-dashed border-border bg-card p-8 sm:p-12 text-center noise"
      data-testid="crate-empty-state"
    >
      <div className="mx-auto w-12 h-12 rounded-full bg-secondary grid place-items-center mb-4">
        <Sparkles className="w-6 h-6 text-[hsl(var(--primary))]" />
      </div>
      <h3 className="font-display text-xl font-semibold">Your collection is empty</h3>
      <p className="text-sm text-muted-foreground mt-1.5 max-w-md mx-auto">
        Upload some music screenshots and we&apos;ll file them into genre crates with buy + preview links.
      </p>
      <div className="mt-5 flex items-center justify-center gap-2 flex-wrap">
        <Link to="/upload">
          <Button
            className="rounded-xl border-2 border-[#FFB24A] !bg-transparent text-[#FFB24A] hover:!bg-[#FFB24A]/35 hover:text-white active:!bg-[#FFB24A]/35 active:text-white focus-visible:!bg-[#FFB24A]/35 focus-visible:text-white focus-visible:ring-[#FFB24A] hover:shadow-[0_0_24px_-8px_#FFB24A] transition-colors duration-150"
            data-testid="crate-empty-cta"
          >
            <Upload className="w-4 h-4 mr-2" /> Scan Screenshots
          </Button>
        </Link>
        <SnapAndSeekButton testid="crate-empty-snap-and-seek" />
      </div>
    </div>
  );
}
